import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useAddProjectLink, useDeleteProjectLink } from "../creatorServices";
import { isBlank, notBlank } from "../../util/ext";
import { map, without } from "ramda";
import { useCurrentOrgId, useIsAdmin } from "../../auth/authUtils";
import { useCsps } from "../cspServices";
import { getProjectUrl } from "../../manage-csp/cspUtils";

import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import OrganizationAutoComplete from "../../user-admin/OrganizationAutoComplete";

const filter = createFilterOptions();

const ProjectLink = props => {
  const { id, projectLink, showAlert } = props;
  const currentOrgId = useCurrentOrgId();
  const isAdmin = useIsAdmin();

  const initialOrgId =
    projectLink?.organizationId ?? (!isAdmin ? currentOrgId : "");

  const [orgId, setOrgId] = useState(initialOrgId);
  const [projectLinks, setProjectLinks] = useState([]);
  const [link, setLink] = useState(projectLink?.link ?? "");
  const [editVisible, setEditVisible] = useState(notBlank(projectLink));

  const cspResults = useCsps();
  useEffect(() => {
    if (cspResults.isSuccess) {
      setProjectLinks(
        without("Legacy URL from S3", map(getProjectUrl, cspResults.data)),
      );
    }
  }, [cspResults.isSuccess, cspResults.data]);

  const addProjectLink = useAddProjectLink();
  const handleSaveClick = useCallback(async () => {
    try {
      if (isBlank(orgId)) {
        throw new Error("Please select an organization");
      } else if (isBlank(link)) {
        throw new Error("Please provide a CSP project link");
      } else {
        await addProjectLink.mutateAsync({
          data: {
            mediaId: id,
            organizationId: orgId,
            link,
          },
          resourceIds: {
            id: id,
          },
        });
      }

      showAlert("success", ["CSP Project Link Saved"]);
    } catch (err) {
      showAlert("error", [err.message]);
    }
  }, [addProjectLink, orgId, id, link, showAlert]);

  const deleteProjectLink = useDeleteProjectLink();
  const handleDeleteClick = async () => {
    try {
      await deleteProjectLink.mutateAsync({ resourceIds: { id } });
      setLink("");
      setOrgId(initialOrgId);
      showAlert("success", ["CSP Project Link Deleted"]);
    } catch (err) {
      showAlert("error", [err.message]);
    }
  };

  return (
    <Grid container direction="column" spacing={4} style={{ paddingRight: 5 }}>
      <Grid item component={Box} pb={editVisible ? 1 : 4}>
        <Typography variant="h6">CSP Project Link</Typography>
      </Grid>
      {editVisible ? (
        <Fragment>
          {isAdmin && (
            <Grid item>
              <OrganizationAutoComplete
                disabled={notBlank(projectLink)}
                value={orgId}
                onChange={setOrgId}
                onBlur={() => {}}
                helperText={null}
                error={false}
              />
            </Grid>
          )}
          <Grid item>
            <Autocomplete
              id="project-link"
              freeSolo
              disabled={notBlank(projectLink)}
              options={projectLinks}
              value={link}
              onChange={(event, value) => {
                setLink(value);
              }}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);

                // Suggest the creation of a new value
                if (params.inputValue !== "") {
                  filtered.push(params.inputValue);
                }

                return filtered;
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  fullWidth
                  variant="standard"
                  label="CSP Project Link"
                />
              )}
            />
          </Grid>

          <Grid item>
            {notBlank(projectLink) ? (
              <Button
                color="primary"
                variant="outlined"
                onClick={handleDeleteClick}
              >
                Delete Project Link
              </Button>
            ) : (
              <Button
                color="primary"
                variant="outlined"
                onClick={handleSaveClick}
              >
                Save Project Link
              </Button>
            )}
          </Grid>
        </Fragment>
      ) : (
        <Button
          variant="outlined"
          color="primary"
          onClick={() => setEditVisible(true)}
          style={{ width: "fit-content", marginLeft: 10 }}
        >
          Add Project Link
        </Button>
      )}
    </Grid>
  );
};

export default ProjectLink;
